import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  async (config) => {
    try {
      const { Auth } = await import('aws-amplify');
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.log('No auth token available');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const serverMsg: string = error.response?.data?.error || error.response?.data?.message || '';
      // Missing tenant context means the token is valid but custom:tenantId hasn't propagated
      // yet — syncMyRole will fix it on the next auth check, so don't redirect.
      const isTenantMissing = serverMsg.toLowerCase().includes('tenant context');
      if (!isTenantMissing) {
        window.location.href = '/';
      }
    }
    if (error.response?.data?.error) {
      error.message = error.response.data.error;
    } else if (error.response?.data?.message) {
      error.message = error.response.data.message;
    }
    return Promise.reject(error);
  }
);

export interface TeamMember {
  name: string;
  role: string;
  jobTitle?: string;
}

export interface Assessment {
  tenantId: string;
  assessmentId: string;
  assessmentType: 'CMA' | 'CMDA';
  assessmentName: string;
  assessorId: string;
  assessorName: string;
  status: 'draft' | 'submitted';
  createdAt: string;
  updatedAt: string;
  controlResponses?: ControlResponse[];
  monitoringResponses?: MonitoringResponse[];
  overallScore?: 'Adequate' | 'Inadequate';
  teamMembers?: string; // Simple text string
  finalAssessmentComment?: string;
  // CMDA specific fields - CDA (Control Design Assessment)
  controlDesignRating?: 'Adequate' | 'Inadequate';
  controlIssueId?: string;
  designCompletionDate?: string;
  facilitatorName?: string;
  facilitatorEmployeeId?: string;
  facilitatorKnowledgeable?: 'Yes' | 'No';
  designParticipants?: string; // Comma-separated SOEIDs
  controlPerformerRepresentative?: string; // Comma-separated SOEIDs
  controlId?: string;
  controlDescription?: string;
  regulationObligationIds?: string; // All Regulation/obligation/ID's mapped to this Control
  // CMDA specific fields - MDA (Monitoring Design Assessment)
  monitoringDesignRating?: 'Adequate' | 'Inadequate';
  monitoringIssueId?: string;
  monitoringToolId?: string;
  monitoringToolDescription?: string;
  // CMA specific fields
  processId?: string;
  processDescription?: string;
  activityDescription?: string;
  riskIds?: string[];
  riskConsequences?: string;
  processInitialReview?: string;
  processProposedChanges?: string;
  riskInitialReview?: string;
  riskProposedChanges?: string;
  // CMA linking fields
  linkedCmdaId?: string;
  cmaControlIds?: string;
  cmaMonitoringIds?: string;
  cmaType?: 'remediation' | 'change';
}

export interface ControlResponse {
  questionId: string;
  question: string;
  response: 'Y' | 'N' | 'NA' | null | string; // string for CMA text responses
  comments?: string;
  initialReview?: string; // For CMA: Initial Review field
  proposedChanges?: string; // For CMA: Proposed changes (renamed from comments)
}

export interface MonitoringResponse {
  questionId: string;
  question: string;
  response: 'Y' | 'N' | 'NA' | null | string; // string for CMA text responses
  comments?: string;
  initialReview?: string; // For CMA: Initial Review field
  proposedChanges?: string; // For CMA: Proposed changes (renamed from comments)
}

export interface User {
  tenantId: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'assessor' | 'viewer';
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

export interface Entitlement {
  tenantId: string;
  maxUsers: number;
  currentUsers: number;
  canCreateUser: boolean;
  status: string;
  subscriptionPlan?: string;
  hasActiveEntitlement?: boolean;
  lastSyncAt?: string;
  expirationDate?: string | null;
}

export const apiService = {
  // Assessment APIs
  async getAssessments(): Promise<Assessment[]> {
    const response = await api.get('/assessments');
    return response.data;
  },

  async getAssessment(assessmentId: string): Promise<Assessment> {
    const response = await api.get(`/assessments/${assessmentId}`);
    return response.data;
  },

  async createAssessment(assessment: Partial<Assessment>): Promise<Assessment> {
    const response = await api.post('/assessments', assessment);
    return response.data;
  },

  async updateAssessment(assessmentId: string, assessment: Partial<Assessment>): Promise<Assessment> {
    const response = await api.put(`/assessments/${assessmentId}`, assessment);
    return response.data;
  },

  async deleteAssessment(assessmentId: string): Promise<void> {
    await api.delete(`/assessments/${assessmentId}`);
  },

  // User APIs
  async getUsers(): Promise<User[]> {
    const response = await api.get('/users');
    return response.data;
  },

  async getUser(userId: string): Promise<User> {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  async createUser(user: Partial<User>): Promise<User> {
    const response = await api.post('/users', user);
    return response.data;
  },

  async updateUser(userId: string, user: Partial<User>): Promise<User> {
    const response = await api.put(`/users/${userId}`, user);
    return response.data;
  },

  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/users/${userId}`);
  },

  async syncMyRole(): Promise<{ role: string; tenantId: string; industryProfile: string; promoted: boolean }> {
    const response = await api.post('/users/sync-role', {});
    return response.data;
  },

  // Entitlement APIs
  async getEntitlement(): Promise<Entitlement> {
    const response = await api.get('/entitlements');
    return response.data;
  },

  // Tenant APIs
  async getAvailableTenants(): Promise<any[]> {
    const response = await api.get('/tenants/available');
    return response.data;
  },

  async joinTenant(tenantId: string): Promise<void> {
    await api.post('/tenants/join', { tenantId });
  },

  // Export APIs
  async exportAssessment(assessmentId: string, format: 'excel' | 'pdf'): Promise<{ fileUrl: string }> {
    const response = await api.post('/exports', {
      assessmentId,
      format,
    });
    return response.data;
  },
};

export default apiService;

